import { Box, Divider, Typography } from '@mui/material';
import { initialAlertState, useAlertStatus } from 'customHook/useAlertStatus';
import React, { useState, useEffect, useReducer, useCallback } from 'react';
import zeplinColor from 'theme/Color';
import CustomDrawer from 'components/shared/CustomDrawer/CustomDrawer';
import CustomAlert from 'components/shared/CustomAlert/CustomAlert';
import { Lead, LeadKanbanColumnId } from '../LeadsKanbanColumn';
import { HeaderActions } from './components/HeaderActions';
import { LeadFields } from './components/LeadFields';
import { LeadHeader } from './components/LeadHeader';
import { LeadAttachments } from './components/LeadAttachement';
import { LeadsService } from 'service/leads.service';
import { getAssigneeDropdown, setEditLeadData } from 'store/slices/leadsSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import { modalReducer } from 'reducers/modalReducer';
import { useLeadFiles } from '../customHook/useLeadAttachment';
import CustomConvertModal from './components/LeadConvertToClient';
import { CompleteOperationType } from 'model/alerts.model';

const STATUS_COLOR_MAP: Record<LeadKanbanColumnId, string> = {
  open: zeplinColor.Neutral8,
  contacted: zeplinColor.AlertInfo,
  'no-response': zeplinColor.Blue,
  responded: zeplinColor.Success,
  qualified: zeplinColor.Success,
  unqualified: zeplinColor.Neutral4,
};

const statusLabels: Record<LeadKanbanColumnId, string> = {
  open: 'Open',
  contacted: 'Contacted',
  'no-response': 'No Response',
  responded: 'Responded',
  qualified: 'Qualified',
  unqualified: 'Unqualified',
};

const CustomViewLead = ({
  setDrawerState,
  drawerState,
  selectedData,
  setSelected,
  onDelete,
  onUpdate,

  onComplete,
  fetchLeads,
}: {
  setDrawerState: React.Dispatch<React.SetStateAction<boolean>>;
  drawerState: boolean;
  selectedData: Lead | null;

  setSelected: React.Dispatch<React.SetStateAction<Lead | null>>;
  onStatusChange?: (leadId: string, status: LeadKanbanColumnId) => void;
  onDelete?: (leadId: string, onComplete?: () => void) => void;
  onUpdate?: () => void;
  onComplete: CompleteOperationType;
  fetchLeads: () => Promise<void>;
}) => {
  const dispatch = useAppDispatch();
  const { assigneeDropdown } = useAppSelector((state) => state.leads);

  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState<any>({ status: [''] });
  const [fileDownloadUrls, setFileDownloadUrls] = useState<Record<string, string>>({});
  const { alertState, completeOperation, getAlertContent, setAlertState } = useAlertStatus();
  const [modalState, modalDispatch] = useReducer(modalReducer, {});
  const [isConverting, setIsConverting] = useState(false);

  const handleConvertToClient = async () => {
    if (!selectedData?.leadId) return;
    setIsConverting(true);
    try {
      // await LeadsService.convertLeadToClient(selectedData.leadId);
      modalDispatch({ type: 'CLOSE', payload: 'convert' });
      setDrawerState(false);
      onUpdate?.();
      onComplete('update', 'Lead converted to client successfully', false);
      fetchLeads();
    } catch {
      onComplete('update', 'Failed to convert lead to client', true);
    } finally {
      setIsConverting(false);
    }
  };
  const onClose = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent) => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setDrawerState(open);
  };

  const handleModalConfirm = async () => {
    if (!selectedData?.leadId || !onDelete) return;

    setLoading(true);

    await onDelete(selectedData.leadId, () => {
      setLoading(false);
      setDrawerState(false);
    });
  };

  const handleEditLead = () => {
    if (selectedData) {
      dispatch(setEditLeadData(selectedData));
    }
  };

  const leaddropdown = async () => {
    try {
      const response = await LeadsService.getLeadTypeDropdowns(selectedData?.leadTypeName || '');
      dispatch(getAssigneeDropdown());

      setOptions({ status: response.status || [''] });
    } catch (error) {
      console.log({ error });
    }
  };
  const handleFieldUpdate = async (fieldKey: string, value: { Name: string; Id: string }) => {
    if (!selectedData?.leadId) return;
    try {
      const param =
        fieldKey === 'assignee' ? 'ownerName' : fieldKey === 'status' ? 'status' : fieldKey;
      await LeadsService.updateLead(selectedData.leadId, {
        [param]: value.Name,
      });
      onUpdate && onUpdate();
      completeOperation('update', 'Lead updated successfully', false);
    } catch (error) {
      completeOperation('update', 'Failed to update lead', true);
    }
  };
  const handleDelete = () => {
    modalDispatch({ type: 'OPEN', payload: 'delete' });
  };
  const handleAttachmentUpload = async (uploadedRefs: string[]) => {
    if (!selectedData?.leadId || uploadedRefs.length === 0) return;

    const existing = Array.isArray((selectedData as any)?.attachments)
      ? ((selectedData as any).attachments as string[])
      : [];

    await LeadsService.updateLead(selectedData.leadId, {
      attachments: {
        added: [...existing, ...uploadedRefs],
        removed: [],
      },
    });

    onUpdate?.(); // refresh list + selected lead
    completeOperation('upload', 'Attachments uploaded successfully', false);
  };
  const { selectedFiles, onDrop, deleteFile, setSelectedFiles } = useLeadFiles(
    selectedData?.leadId,
    (selectedData as any)?.attachments || [],
    handleAttachmentUpload,
  );
  const getAllFiles = useCallback(async () => {
    if (!selectedData?.leadId) return;

    try {
      const response = await LeadsService.getAllLeadFiles(selectedData.leadId);
      const files = response?.data?.files || [];

      const nextDownloadUrls = files.reduce((acc: Record<string, string>, file: any) => {
        const downloadUrl = file?.downloadUrl || file?.signedUrl || file?.url;
        if (!downloadUrl) return acc;

        if (file?.fileId) acc[file.fileId] = downloadUrl;
        if (file?.fileName) acc[file.fileName] = downloadUrl;
        if (file?.key) acc[file.key] = downloadUrl;
        if (file?.s3Key) acc[file.s3Key] = downloadUrl;

        return acc;
      }, {});

      setFileDownloadUrls(nextDownloadUrls);
    } catch (error) {
      completeOperation('fetch', 'Failed to fetch attachments', true);
    }
  }, [selectedData?.leadId]);

  useEffect(() => {
    getAllFiles();
  }, []);
  const handleAttachmentDelete = useCallback(
    async (index: number) => {
      if (!selectedData?.leadId) return;

      const fileToRemove = selectedFiles[index];
      const isExistingFile = typeof fileToRemove === 'string';
      const originalFiles = [...selectedFiles];

      deleteFile(index);

      try {
        const remainingAttachments = originalFiles.filter((_, ind) => ind !== index);
        const removedAttachments = isExistingFile ? [fileToRemove as string] : [];

        await LeadsService.updateLead(selectedData.leadId, {
          attachments: {
            added: remainingAttachments.filter((att: any) => typeof att === 'string'),
            removed: removedAttachments,
          },
        });

        setSelected((prev) =>
          prev
            ? ({
                ...prev,
                attachments: remainingAttachments.filter((att: any) => typeof att === 'string'),
              } as Lead)
            : prev,
        );

        completeOperation('delete', 'Attachment deleted successfully', false);
        onUpdate?.();
      } catch (err: any) {
        setSelectedFiles(originalFiles);
        completeOperation(
          'delete',
          err?.response?.data?.error || 'Failed to delete attachment',
          true,
        );
      }
    },
    [selectedData, selectedFiles, deleteFile, setSelectedFiles, completeOperation, onUpdate],
  );
  const handleAttachmentDownload = useCallback(
    async (index: number) => {
      const file = selectedFiles[index];
      if (!file) return;

      if (file instanceof File) {
        const localUrl = URL.createObjectURL(file);
        const link = document.createElement('a');
        link.href = localUrl;
        link.download = file.name;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(localUrl);
        return;
      }

      const rawValue = String(file);
      const fileName = rawValue.split('/').pop() || 'attachment';
      const downloadUrl =
        fileDownloadUrls[rawValue] ||
        fileDownloadUrls[fileName] ||
        fileDownloadUrls[selectedData?.leadId || ''];

      if (!downloadUrl) {
        completeOperation('download', 'Download URL not found', true);
        return;
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    [selectedFiles, fileDownloadUrls, selectedData?.leadId, completeOperation],
  );
  useEffect(() => {
    selectedData?.leadTypeName && leaddropdown();
  }, [selectedData]);

  if (!selectedData) return null;

  return (
    <CustomDrawer state={drawerState} onClose={onClose(false)}>
      <Box sx={{ width: { xs: '427px', xl: '527px' }, position: 'relative', overflowY: 'scroll' }}>
        <Box
          sx={{ position: 'sticky', top: 0, backgroundColor: zeplinColor.Neutral1, zIndex: '1' }}
        >
          <HeaderActions
            onEdit={handleEditLead}
            onDelete={handleDelete}
            onClose={onClose(false)}
            onConvert={() => modalDispatch({ type: 'OPEN', payload: 'convert' })}
          />
          <Divider
            component='div'
            sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
          />
        </Box>

        <Box sx={{ padding: { xs: '20px', xl: '24px' } }}>
          <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '24px' }, flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: { xs: '16px', xl: '32px' }, flexDirection: 'column' }}>
              <LeadHeader selectedData={selectedData} />

              <Divider
                component='div'
                sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                Lead Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <LeadFields
                  icon='/assets/lead-client-icon.svg'
                  fieldName='Client name'
                  fieldValue={selectedData?.name || 'N/A'}
                  isProfile
                  src={selectedData?.profile_img_url || ''}
                />
                <LeadFields
                  icon='/assets/lead-status-icon.svg'
                  fieldName='Status'
                  fieldValue={selectedData?.status || 'N/A'}
                  isDropdown
                  isChip
                  dropdownOptions={
                    options?.status?.map((status: string) => ({
                      Name: status,
                      Id: status,
                      color: STATUS_COLOR_MAP[status as LeadKanbanColumnId],
                    })) || []
                  }
                  onDropdownSelect={handleFieldUpdate}
                />
                <LeadFields
                  icon='/assets/user-multiple.svg'
                  fieldName='Assignee'
                  fieldValue={selectedData?.ownerName || 'N/A'}
                  isProfile
                  src={selectedData?.ownerProfileImg || ''}
                  isDropdown
                  dropdownHeading='Change Assignee'
                  dropwdownSearch
                  dropdownOptions={assigneeDropdown}
                  onDropdownSelect={handleFieldUpdate}
                />
                <LeadFields
                  icon='/assets/lead-type-icon.svg'
                  fieldName='Lead Type'
                  fieldValue={selectedData?.leadTypeName || 'N/A'}
                  isChip
                />
              </Box>
            </Box>
            <Divider
              component='div'
              sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <LeadFields
                  icon='/assets/lead-level-icon.svg'
                  fieldName='Level'
                  fieldValue={selectedData?.level || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-college-icon.svg'
                  fieldName='College'
                  fieldValue={selectedData?.college || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-draft-icon.svg'
                  fieldName='Draft Eligible'
                  fieldValue={selectedData?.draftEligible || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-type-icon.svg'
                  fieldName='Redshirt'
                  fieldValue={selectedData?.redshirt || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-sport-icon.svg'
                  fieldName='Sport'
                  fieldValue={selectedData?.sport || 'N/A'}
                  isChip
                />
                <LeadFields
                  icon='/assets/lead-position-icon.svg'
                  fieldName='Position'
                  fieldValue={selectedData?.position || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-class-icon.svg'
                  fieldName='Class'
                  fieldValue={selectedData?.class || 'N/A'}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                Recruiting Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <LeadFields
                  icon='/assets/lead-draft-projection-icon.svg'
                  fieldName='Draft projection'
                  fieldValue={selectedData?.draftProjection || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-decision-maker-icon.svg'
                  fieldName='Decision maker'
                  fieldValue={selectedData?.decisionMaker || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-pff-icon.svg'
                  fieldName='Pff ovr grade'
                  fieldValue={selectedData?.pffOverallGrade || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-agency-icon.svg'
                  fieldName='Agency'
                  fieldValue={selectedData?.agency || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-star-icon.svg'
                  fieldName='247 sport rating'
                  fieldValue={selectedData?.sports247Rating || 'N/A'}
                  isChip
                />
                <LeadFields
                  icon='/assets/lead-manager-icon.svg'
                  fieldName='Manager'
                  fieldValue={selectedData?.manager || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-star-icon.svg'
                  fieldName='Espn rank'
                  fieldValue={selectedData?.espnRank || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-referred-icon.svg'
                  fieldName='Referred by'
                  fieldValue={selectedData?.referredBy || 'N/A'}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                Contact Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <LeadFields
                  icon='/assets/clock-icon.svg'
                  fieldName='Mobile'
                  fieldValue={selectedData?.phone || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-mail-icon.svg'
                  fieldName='Email'
                  fieldValue={selectedData?.email || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-address-icon.svg'
                  fieldName='Address'
                  fieldValue={selectedData?.address || 'N/A'}
                />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                Social
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <LeadFields
                  icon='/assets/lead-instagram-icon.svg'
                  fieldName='Instagram'
                  fieldValue={selectedData?.instagram || 'N/A'}
                />
                <LeadFields
                  icon='/assets/lead-twitter-icon.svg'
                  fieldName='Twitter'
                  fieldValue={selectedData?.twitter || 'N/A'}
                />
              </Box>
            </Box>
            <Divider
              component='div'
              sx={{ borderColor: zeplinColor.Neutral5, position: 'relative' }}
            />
            <LeadAttachments
              files={selectedFiles}
              onFileSelect={async (files) => {
                try {
                  await onDrop(files);
                } catch {
                  completeOperation('upload', 'Failed to upload attachments', true);
                }
              }}
              onFileDelete={handleAttachmentDelete}
              onFileDownload={handleAttachmentDownload}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
              <Typography variant='responsiveHeading3' sx={{ color: zeplinColor.BasicBlack }}>
                System Information
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                <LeadFields
                  icon='/assets/created-by-icon.svg'
                  fieldName='Created by'
                  fieldValue={selectedData?.createdBy || 'N/A'}
                />
                <LeadFields
                  icon='/assets/calendar-icon.svg'
                  fieldName='Created date'
                  fieldValue={
                    selectedData?.createdAt
                      ? new Date(selectedData.createdAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'
                  }
                />
                <LeadFields
                  icon='/assets/clock-icon.svg'
                  fieldName='Last modified date'
                  fieldValue={
                    selectedData?.updatedAt
                      ? new Date(selectedData.updatedAt).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'
                  }
                />
              </Box>
            </Box>
          </Box>
        </Box>
        <CustomConvertModal
          isModalOpen={modalState.convert || false}
          modalDispatch={modalDispatch}
          onConvert={handleConvertToClient}
          item={selectedData || null}
        />

        <CustomDeleteModal
          isModalOpen={modalState.delete || false}
          modalDispatch={modalDispatch}
          onDelete={handleModalConfirm}
          title='Delete Lead?'
          itemName={selectedData?.name}
          description={`Are you sure you want to delete ${selectedData?.name}? This action cannot be undone.`}
          isDeleting={loading}
        />
      </Box>
      <CustomAlert
        open={alertState.status !== 'idle'}
        handleClose={() => setAlertState(initialAlertState)}
        message={getAlertContent(alertState)}
        error={alertState.status === 'error'}
      />
    </CustomDrawer>
  );
};

export default CustomViewLead;
